// Scada.js
"use client";
import React, { useRef, useEffect, useState } from "react";
import { useScroll, useTransform, motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import "./../styles/Elecinstallation.css";

import Header from './Header';
import Footer from './Footer';
import Beams from "./Beams";

import image1 from "../assets/sdaca12A.jpeg";
import image2 from "../assets/sdaca3A.jpg";
import image3 from "../assets/sdaca10A.jpeg";
import image4 from "../assets/sdaca3A.jpg";
import image5 from "../assets/sdaca6A.jpeg";
import image6 from "../assets/sdaca1A.jpg";
import image7 from "../assets/sdaca12A.jpeg";
import image8 from "../assets/sdaca10A.jpeg";

const ElecDesignHeroSection = ({ data }) => {
  const timelineRef = useRef(null);
  const containerRef = useRef(null);
  const [timelineHeight, setTimelineHeight] = useState(0);

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  useEffect(() => {
    const calculateAndUpdateTimelineHeight = () => {
      if (timelineRef.current) {
        const newHeight = timelineRef.current.getBoundingClientRect().height;
        setTimelineHeight(prevHeight => {
          if (newHeight > 0 && (newHeight !== prevHeight || prevHeight === 0)) {
            return newHeight;
          }
          return prevHeight;
        });
      }
    };

    const handleLayoutReady = () => {
      calculateAndUpdateTimelineHeight();
      AOS.refresh();
    };

    const initialCalcTimer = setTimeout(calculateAndUpdateTimelineHeight, 50);

    if (document.readyState === "complete") {
      const readyTimer = setTimeout(handleLayoutReady, 100);
      return () => clearTimeout(readyTimer);
    } else {
      window.addEventListener("load", handleLayoutReady);
    }

    window.addEventListener("resize", handleLayoutReady);

    return () => {
      clearTimeout(initialCalcTimer);
      window.removeEventListener("load", handleLayoutReady);
      window.removeEventListener("resize", handleLayoutReady);
    };
  }, [data]);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 10%", "end 50%"],
  });

  const heightTransform = useTransform(
    scrollYProgress,
    [0, 1],
    [0, timelineHeight]
  );
  const opacityTransform = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  return (
    <div className="elec-design-hero-container" ref={containerRef}>
      <div className="elec-design-hero-header">
        <h2 className="elec-design-hero-title">SCADA</h2>
        <p className="elec-design-hero-description">
          SCADA systems enable real-time monitoring and control of industrial processes, ensuring operational efficiency, safety, and data-driven decision-making across critical infrastructure.
        </p>
      </div>

      <div ref={timelineRef} className="elec-design-hero-timeline">
        {data.map((item, index) => (
          <div key={index} className="elec-design-hero-step">
            <div className="elec-design-hero-sticky">
              <div className="elec-design-hero-marker-outer">
                <div className="elec-design-hero-marker-inner" />
              </div>
              <h3 className="elec-design-hero-step-title-desktop">{item.title}</h3>
            </div>

            <div className="elec-design-hero-step-content">
              <h3 className="elec-design-hero-step-title-mobile">{item.title}</h3>
              <img
                src={item.image}
                alt={item.title}
                className="elec-design-hero-step-image"
                data-aos="fade-left"
              />
              <div className="elec-design-hero-step-paragraph" data-aos="fade-left">
                {typeof item.paragraph === "string" ? (
                  <p>{item.paragraph}</p>
                ) : (
                  <>
                    <p>{item.paragraph?.text}</p>
                    {Array.isArray(item.paragraph?.points) &&
                      item.paragraph.points.map((point, idx) => (
                        <p key={idx} className="elec-design-hero-step-point">
                          <span className="elec-design-hero-arrow-icon">→</span> {point}
                        </p>
                      ))}
                  </>
                )}
              </div>
            </div>
          </div>
        ))}

        <div
          style={{ height: `${timelineHeight}px` }}
          className="elec-design-hero-progress-bar-wrapper"
        >
          <motion.div
            style={{
              height: heightTransform,
              opacity: opacityTransform,
            }}
            className="elec-design-hero-progress-bar"
          />
        </div>
      </div>
    </div>
  );
};

const Scada = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const timelineData = [
    {
      title: "What can scada do and why to use in Distribution system?",
      image: image7,
      paragraph: {
        points: [
          "  New sites typically implement a SCADA system to monitor and control substations and related equipment. However, older facilities can also benefit by adding a SCADA system or by upgrading an existing SCADA system to take advantage of newer technologies.",
          "  Not only will a new or upgraded SCADA system provide better monitoring and control, it can also extend the life of substations and related equipment by providing current data for troubleshooting small problems before they escalate. Furthermore, the ample historical data provided by SCADA systems can improve preventive maintenance scheduling and cut associated costs."
        ]
      }
    },
    {
      title: "Why are SCADA systems important?",
      image: image8,
      paragraph: {
        points: [
          "	Electrical distribution systems involve many remote applications and sites, and monitoring and controlling these sites has often been difficult. To solve this problem, utilities began installing remote terminal/telemetry units (RTUs) at substations. Early RTUs were initially custom-made units, but later versions relied on standard hardware such as programmable logic controllers (PLCs) or industrial PCs. Intelligent electronic devices (IEDs) are a more recent technology development, and these devices are now installed at most substations to some extent. These IEDs generally communicate with the substation RTU.",
          "	Power distribution to various electrical loads at substations is controlled by switchgear feeders. Sensors mounted on the switchgear collect various data on current, voltage, power and switchgear status. This data is transferred to the RTU, which is in turn polled by a SCADA system.",
          "	The SCADA system consists of a master control station with one or more PC-based human machine interfaces (HMIs). The SCADA system may also contain other secondary control stations with HMIs, and large substations may also have local HMIs.",
          "	Operators view information on the HMIs to monitor and control substation operations and related equipment. Modern SCADA systems are extremely adept at handling the large amounts of data necessary to monitor the electrical state of all power lines, cables and equipment."

        ]
      }
    },
  ];

  return (
    <>
      <Header />
      <div className="elec-installation-page-container">
        <div className="epc-beam-background">
           <Beams
            beamWidth={1.4}
            beamHeight={30}
            beamNumber={14}
            lightColor="#00CCFF"
            speed={20}
            noiseIntensity={0}
            scale={0.19}
            rotation={59}
          />
        </div>
        <div className="elec-installation-page-header">
          <h1>SUPERVISORY CONTROL AND DATA AQUISITION</h1>
          <p>
              Substations are a critical component for maintaining electrical supply and load control in low voltage, medium voltage and high voltage electrical distribution networks. In order to ensure the proper functioning of substations and related equipment such as line-mounted switches and capacitors, most utilities use SCADA systems to automate monitoring and control.
          </p>
        </div>

        <div className="elec-installation-cube-wrapper">
          <div className="elec-installation-cube-gallery">
            <img src={image1} alt="Engineering Slide 1" />
            <img src={image2} alt="Engineering Slide 2" />
            <img src={image3} alt="Engineering Slide 3" />
            <img src={image4} alt="Engineering Slide 4" />
            <img src={image5} alt="Engineering Slide 5" />
            <img src={image6} alt="Engineering Slide 6" />
          </div>
        </div>

        
      </div>

      <ElecDesignHeroSection data={timelineData} />
      <Footer />
    </>
  );
};

export default Scada;
